import axios from 'axios';
import { CONF } from './environment';
import { processHostedZoneName } from './zones';
import { removeSpinners } from './sidebar';
import { authInterceptor, isTokenExpiringSoon, logoutFromIdp, refreshAccessToken } from './auth';
import swal from 'sweetalert';
import { addToast, displayErrorPopup, displaySuccessPopup } from './main';
import { getForbiddenMessage } from './api_errors';

export function showError(result, callback, optInfo) {
  // Suppress popup for a 401 — the response interceptor handles it via token refresh + retry or logout. Showing a
  // swal here would be confusing because the interceptor will resolve the situation silently. A 403 that reaches
  // this point is a permission error and is shown.
  // Also suppress AxiosErrors with no response (NetworkError) — these occur
  // when the browser aborts in-flight requests during a logout redirect.
  if (result?.response?.status === 401) {
    console.debug('Suppressing error popup for 401 — interceptor will handle auth recovery.');
    return;
  }
  if (result?.isAxiosError && !result.response) {
    if (!navigator.onLine) {
      swal({ title: 'You are offline', text: 'Please check your internet connection and try again.', icon: 'warning' });
      return;
    }
    console.debug(
      'Suppressing error popup for AxiosError with no response (likely CORS-blocked 403 or request abort during redirect).',
    );
    return;
  }

  removeSpinners();
  console.error(result);

  let errorText;
  if (typeof result === 'string') {
    errorText = result;
  } else if (result.isAxiosError && result.response?.status === 403) {
    errorText = getForbiddenMessage(result);
  } else if (result.isAxiosError && result.response && result.response.data) {
    errorText = result.response.data.message;
  } else if (result.data && result.data.message) {
    errorText = result.data.message;
  }

  if (!errorText) {
    errorText = String(result);
  }

  const errorContent = document.createElement('div');
  errorContent.innerText = errorText;

  swal({
    title: 'Error',
    content: errorContent,
    icon: 'error',
  }).then(function () {
    removeSpinners();
    if (callback) {
      callback(result, optInfo);
    } else {
      return result;
    }
  });
}

const apiAxios = axios.create({
  baseURL: CONF.apiEndpoint,
});

apiAxios.interceptors.request.use(authInterceptor);
apiAxios.interceptors.response.use(...createAuthRefreshInterceptor(apiAxios));

// Isolated axios instance for GAIA chatbot calls.
// Uses the same auth interceptors as apiAxios (token injection + 401/403
// refresh-and-retry) so expired tokens are handled transparently.
const gaiaAxios = axios.create({
  baseURL: CONF.apiEndpoint,
});
gaiaAxios.interceptors.request.use(authInterceptor);
// Duplicate the access token into X-Auth-Token so the Lambda can read the
// real JWT.  API Gateway's TOKEN authorizer masks the Authorization header
// value (replaces it with "****") before passing the event to Lambda.
// We read directly from sessionStorage (same source authInterceptor uses).
gaiaAxios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('user_token');
  if (token) {
    config.headers['X-Auth-Token'] = token;
  }
  return config;
});
gaiaAxios.interceptors.response.use(...createAuthRefreshInterceptor(gaiaAxios));

export { apiAxios, gaiaAxios };

export function loadPermissions() {
  return apiAxios.get('/permissions').then(response => response.data.permissions);
}

/**
 * Retrieves settings for the given key or keys.
 * @param {string | string[] | undefined} key a single key or array of keys to retrieve; if not set then
 *        all settings will be retrieved
 * @returns {Promise<object>} a promise that returns the settings object when completed
 */
export function loadSettings(key) {
  if (!key) {
    // Load all settings
    return apiAxios.get('/settings').then(response => response.data);
  } else if (typeof key === 'string') {
    // Load settings for a single key
    return apiAxios.get('/settings/' + key).then(response => response.data);
  } else if (Array.isArray(key)) {
    // Load settings for multiple keys in parallel, then merge them
    return Promise.all(
      key.map(singleKey => apiAxios.get('/settings/' + singleKey).then(response => response.data)),
    ).then(results => Object.assign({}, ...results));
  } else {
    return Promise.reject('Invalid key for settings');
  }
}

/**
 * @class
 * @param {Axios} apiAxiosInstance
 */
class BaseApiAxios {
  constructor(apiAxiosInstance) {
    // if we want to log in developer console all the request and response,
    // but I personally prefer to check in the network tab
    // apiAxiosInstance.interceptors.request.use(request => {
    //   console.log('Starting Request', JSON.stringify(request, null, 2));
    //   return request;
    // });
    // apiAxiosInstance.interceptors.response.use(response => {
    //   console.log('Response:', JSON.stringify(response, null, 2));
    //   return response;
    // });
    this.axiosInstance = apiAxiosInstance;
    this.invalidateCacheHeaders = { 'Cache-Control': 'max-age=0, must-revalidate' };
  }

  /**
   * @async
   * @name getFeatureFlag
   * @description This call will return the feature flag data for the given feature flag
   * @param {string} featureFlag
   * @returns {Promise<object>} feature flag data
   */
  getFeatureFlag = async featureFlag => {
    const data = await this.get(`feature-flag-state-frontend?name=${encodeURIComponent(featureFlag)}`);
    let featureFlagData = data['feature-flag'];
    if (!featureFlagData) featureFlagData = {};
    return featureFlagData;
  };

  /**
   * @async
   * @name createOrder
   * @description
   * @param {object} payload
   * @returns {Promise<object>} create-order response
   */
  createOrder = async payload => {
    const combined_payload = Object.assign({
      action: 'create-order',
      payload,
    });
    return await this.post('orders', combined_payload);
  };

  /**
   * @async
   * @name deleteNetworkItem
   * @description
   * @param {string} id
   * @param {string} uuid
   * @returns {Promise<object>} The delete network item result from the backend
   */
  deleteNetworkItem = async (id, uuid) => {
    const payload = {
      action: 'delete-network-item',
      network_information: {
        id,
        uuid,
      },
    };
    return await this.post('networkdb', payload, {}, {});
  };

  /**
   * @async
   * @name getAccount
   * @description
   * @param {string} account_id
   * @returns {Promise<object>} account details
   */
  getAccount = async account_id => {
    const data = await this.get('accounts/' + account_id);
    return data?.account_details;
  };

  /**
   * @async
   * @name getAccountPermissions
   * @description This call will return the temporary and permanent permissions of the current user for a given account
   * @param {string} account_id
   * @returns {Promise<object>} temporary and permanent permissions
   */
  getAccountPermissions = async account_id => {
    const data = await this.get('accounts/' + account_id + '/permissions');
    return data;
  };

  /**
   * @async
   * @name getConfigExports
   * @description Fetch the list of completed AWS Config export orders for an account.
   * @param {string} account_id
   * @param {string|null} next_token
   * @returns {Promise<object>} { items, next_token }
   */
  getConfigExports = async (account_id, next_token = null) => {
    const params = next_token ? { next_token } : {};
    return await this.get('accounts/' + account_id + '/config-exports', null, params);
  };

  /**
   * @async
   * @name getConfigExportDownloadUrl
   * @description Generate a presigned S3 download URL for a completed config export.
   * @param {string} account_id
   * @param {string} date_range  Format: YYYY-MM-DD_YYYY-MM-DD
   * @returns {Promise<object>} { url, expires_in, s3_output_location }
   */
  getConfigExportDownloadUrl = async (account_id, date_range) => {
    return await this.get('accounts/' + account_id + '/config-exports/' + date_range + '/download-url');
  };

  /**
   * @typedef {object} AccountM2MClientMapping
   * @property {string} account_id
   * @property {string} client_id The backend stores it lower-cased
   * @property {string|null} [description]
   * @property {string} [item_type]
   * @property {string} [created_date] YYYY-MM-DD
   * @property {string|null} [created_by] user_name of the creator, set by the backend. Missing/null for
   * mappings created before the field existed.
   */

  /**
   * @typedef {object} AccountM2MClientMappingInput
   * @property {string} client_id Required
   * @property {string} [description] Optional
   */

  /**
   * @async
   * @name getAccountM2MClientMappings
   * @description List the M2M client mappings of an account (GET /accounts/{id}/client-mappings).
   * @param {string} account_id
   * @returns {Promise<{items: AccountM2MClientMapping[]}>}
   */
  getAccountM2MClientMappings = async account_id => {
    return await this.get('accounts/' + account_id + '/client-mappings', this.invalidateCacheHeaders);
  };

  /**
   * @async
   * @name createAccountM2MClientMapping
   * @description Map ONE M2M client id to an account (POST /accounts/{id}/client-mappings, HTTP 201). The backend
   * accepts a single object per request, so several client ids need one call each.
   * @param {string} account_id
   * @param {AccountM2MClientMappingInput} mapping
   * @returns {Promise<AccountM2MClientMapping>} the created mapping
   */
  createAccountM2MClientMapping = async (account_id, mapping) => {
    return await this.post('accounts/' + account_id + '/client-mappings', mapping);
  };

  /**
   * @async
   * @name deleteAccountM2MClientMapping
   * @description Remove the mapping of an M2M client id from an account
   * (DELETE /accounts/{id}/client-mappings/{client_id}, HTTP 204 without a body).
   * @param {string} account_id
   * @param {string} client_id
   * @returns {Promise<undefined>}
   */
  deleteAccountM2MClientMapping = async (account_id, client_id) => {
    return await this.delete('accounts/' + account_id + '/client-mappings/' + encodeURIComponent(client_id));
  };

  /**
   * @async
   * @name getAccounts
   * @description
   * @param principal_types
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} account datasets
   */

  getAccessibleAccounts = async (principal_types, headers, params) => {
    let url = 'accounts/accessible';
    if (principal_types) {
      url += '?principal_types=' + principal_types.join(',');
    }
    return await this.get(url, headers, params);
  };

  getAccounts = async (headers, params) => {
    const data = await this.get('accounts', headers, params);
    return { items: data?.account_data, next_token: data?.next_token };
  };

  /**
   * @async
   * @name getCluster
   * @description
   * @param {String>} project_name
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} cluster datasets
   */
  getCluster = async (project_name, headers, params) => {
    const payload = {
      action: 'get-4wheels-cluster',
      project_name,
    };
    return await this.post('fourwheels', payload, headers, params);
  };

  /**
   * @async
   * @name getClusters
   * @description
   * @param {Array<string>} keys
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} cluster datasets
   */
  getClusters = async (headers, params) => {
    return await this.get('fourwheels', headers, params);
  };

  /**
   * @async
   * @name getDXGWs
   * @description
   * @param {Array<string>} attribute_names
   * @returns {Promise<object>} The vpc retrieved from the backend
   */
  getDXGWs = async attribute_names => {
    const payload = {
      action: 'list-network-items',
      attributes: attribute_names,
      item_type: 'DX_GW',
      table_data: {
        length: 500,
      },
    };
    return await this.post('networkdb', payload, {}, {});
  };

  /**
   * @async
   * @name getHostedZone
   * @description
   * @param {string} domain
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} hosted zone dataset
   */
  getHostedZone = async (domain, headers, params) => {
    const processedDomain = processHostedZoneName(domain);
    return await this.get(`dns/domain/${processedDomain}`, headers, params);
  };

  /**
   * @async
   * @name deletedHostedZoneFromDB
   * @description
   * @param {string} domain
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} hosted zone dataset
   */
  deleteHostedZoneFromDB = async (domain, headers, params) => {
    const processedDomain = processHostedZoneName(domain);
    return await this.delete(`dns/domain/${processedDomain}`, headers, params);
  };

  /**
   * @async
   * @name getHostedZones
   * @description
   * @param {Array<string>} keys
   * @param {object} headers
   * @param {object} params
   * @param {boolean} list_all
   * @returns {Promise<object>} hosted zone datasets
   */
  getHostedZones = async (headers, params) => {
    return await this.get('dns', headers, params);
  };

  /**
   * @async
   * @name getProvider
   * @description
   * @param {string} arn
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} idp datasets
   */
  getProvider = async (arn, headers, params) => {
    const arnEncoded = btoa(arn);
    return await this.get(`idp/${arnEncoded}`, headers, params);
  };

  /**
   * @async
   * @name getLogGroups
   * @description
   * @param {string} account_id
   * @param {string} region
   * @param {number} limit
   * @param {string} next_token
   * @param {string} log_group_prefix
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} log group datasets
   */
  getLogGroups = async (account_id, region, limit, next_token, log_group_prefix, headers, params) => {
    return await this.get(
      `/accounts/${account_id}/log-groups?region_name=${region}${limit ? '&limit=' + limit : ''}${
        log_group_prefix ? '&log_group_name_prefix=' + log_group_prefix : ''
      }${next_token ? '&next_token=' + next_token : ''}`,
      headers,
      params,
    );
  };

  /**
   * @async
   * @name getLogGroupSubscriptionFilters
   * @description
   * @param {string} account_id
   * @param {string} log_group_name
   * @param {string} region
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} log group subscription filter datasets
   */
  getLogGroupSubscriptionFilters = async (account_id, log_group_name, region, headers, params) => {
    return await this.get(
      `/accounts/${account_id}/log-groups/subscription_filters?log_group_name=${log_group_name}&region_name=${region}`,
      headers,
      params,
    );
  };

  /**
   * @async
   * @name getNetworkItem
   * @description
   * @param {string} id
   * @param {string} uuid
   * @param {string} item_type
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The delete network item result from the backend
   */
  getNetworkItem = async (id, uuid, item_type, headers, params) => {
    const payload = {
      action: 'get-network-item',
      id,
      uuid,
      item_type,
    };
    return this.post('networkdb', payload, headers, params);
  };

  /**
   * @async
   * @name getNetworkItems
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} network items response
   */
  getNetworkItems = async (payload, headers, params) => {
    return this.post('networkdb', payload, headers, params);
  };

  /**
   * @async
   * @name getOrder
   * @description
   * @param {string} order_id
   * @param {object} headers
   * @param {object} params
   * @param {object} payload
   * @returns {Promise<object>} order details
   */
  getOrder = async (order_id, headers, params, payload = {}) => {
    const combined_payload = Object.assign(
      {
        action: 'get-order-item',
        id: order_id,
      },
      payload,
    );
    return await this.post(`orders`, combined_payload, headers, params);
  };

  /**
   * @name getOrders
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} order datasets
   */
  getOrders = async (payload, headers, params) => {
    const combined_payload = Object.assign(
      {
        action: 'list-orders',
      },
      payload,
    );
    return await this.post(`orders`, combined_payload, headers, params);
  };

  /**
   * @async
   * @name getRestrictions
   * @description
   * @param {string} account_id
   * @returns {Promise<object>} organizations response
   */
  getRestrictions = async account_id => {
    const [scps, rcps] = await Promise.all([
      this.get('/accounts/' + account_id + '/scps'),
      this.get('/accounts/' + account_id + '/rcps'),
    ]);

    // Merge OU-attached policies: SCP response uses `scps` array per OU, RCP response uses `rcps` array per OU
    const ouAttachedPolicies = {};
    Object.entries(scps.ou_attached_scps || {}).forEach(([ouId, ouData]) => {
      ouAttachedPolicies[ouId] = { policies: ouData.scps || [] };
    });
    Object.entries(rcps.ou_attached_rcps || {}).forEach(([ouId, ouData]) => {
      if (!ouAttachedPolicies[ouId]) ouAttachedPolicies[ouId] = { policies: [] };
      ouAttachedPolicies[ouId].policies = ouAttachedPolicies[ouId].policies.concat(ouData.rcps || []);
    });

    return {
      directly_attached_policies: [...(scps.directly_attached_scps || []), ...(rcps.directly_attached_rcps || [])],
      ou_attached_policies: ouAttachedPolicies,
    };
  };

  /**
   * @async
   * @name getSettings
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} settings response
   */
  getSettings = async (payload, headers, params) => {
    return await this.post('settings', payload, headers, params);
  };

  /**
   * @async
   * @name getSettingsByKey
   * @description
   * @param {Array<string>} keys
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} settings response
   */
  getSettingsByKey = async (keys, headers, params) => {
    if (Array.isArray(keys)) {
      return Promise.all(keys.map(key => this.get('/settings/' + key, headers, params))).then(results =>
        Object.assign({}, ...results),
      );
    } else {
      return Promise.reject('Invalid key for settings');
    }
  };

  /**
   * @async
   * @name getM2MClientPortalAPIAccessInfo
   * @description
   * @param {string} optionType
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} settings response
   */
  getM2MClientPortalAPIAccessInfo = async list_all => {
    return await this.get('/m2m-client-api-accesses?list_all=' + list_all, this.invalidateCacheHeaders);
  };

  /**
   * @async
   * @name getFourwheelsClusterOptions
   * @description
   * @param {string} optionType
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} settings response
   */
  getFourwheelsClusterOptions = async optionType => {
    return await this.get('/fourwheels-cluster-options/' + optionType, this.invalidateCacheHeaders);
  };

  /**
   * @async
   * @name getFourwheelsClusterOptions
   * @description
   * @param {string} optionType
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} settings response
   */
  deleteFourwheelsClusterOption = async optionId => {
    return await this.delete('/fourwheels-cluster-option/' + optionId);
  };

  /**
   * @async
   * @name addOrUpdateFourwheelsClusterOptions
   * @description
   * @param {string} optionType
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} settings response
   */
  addOrUpdateFourwheelsClusterOption = async (payload, action, headers, params) => {
    const body = {
      action: action === 'create' ? 'add-fourwheels-cluster-option' : 'update-fourwheels-cluster-option',
      option: payload,
    };
    return await this.post('settings', body, headers, params);
  };

  /**
   * @name getTargetOUs
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} target ous for account
   */
  getTargetOUs = async (payload, headers, params) => {
    return await this.post(`organizations/target-ous`, payload, headers, params);
  };

  /**
   * @async
   * @returns {Promise<object>} DNS domainconfig from the backend
   */
  getDnsConfig = async () => {
    return await this.get('dns/domainconfig', null, null);
  };

  /**
   * @async
   * @name getVPC
   * @description
   * @param {string} vpc_id
   * @param {string} account_id
   * @param {boolean} extended
   * @returns {Promise<object>} vpc details
   */
  getVPC = async (vpc_id, account_id, extended) => {
    const data = await this.get(
      account_id ? 'accounts/' + account_id + '/vpcs/' + vpc_id : 'vpcs/' + vpc_id,
      null,
      extended ? { extended: true } : null,
    );
    return data?.vpc;
  };

  /**
   * @async
   * @name getVPCs
   * @description
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The vpcs as `items` retrieved from the backend via DynamoDB and eventually the `next_token`
   */
  getVPCs = async (headers, params) => {
    return this.get('vpcs', headers, params);
  };

  /**
   * @async
   * @name getWhitelistedServices
   * @description
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The whitelisted services
   */
  getWhitelistedServices = async (headers, params) => {
    return await this.get('marketplace-products', headers, params);
  };

  /**
   * @async
   * @name postApiKeyData
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} api key datasets
   */
  postApiKeyData = async (payload, headers, params) => {
    return await this.post('keys', payload, headers, params);
  };

  /**
   * @async
   * @name postClusterData
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} cluster datasets
   */
  postClusterData = async (payload, headers, params) => {
    return await this.post('fourwheels', payload, headers, params);
  };

  /**
   * @async
   * @name postDnsData
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} dns response
   */
  postDnsData = async (payload, headers, params) => {
    return await this.post('dns', payload, headers, params);
  };

  /**
   * @async
   * @name postNetworkData
   * @description
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} network response
   */
  postNetworkData = async (payload, headers, params) => {
    return await this.post('networkdb', payload, headers, params);
  };

  /**
   * @async
   * @name triggerAcceptProposal
   * @description
   * @param {string} dx_gateway_id
   * @param {string} proposal_id
   * @param {string} associated_gateway_id
   * @returns {Promise<object>} The delete network item result from the backend
   */
  triggerAcceptProposal = async (dx_gateway_id, proposal_id, associated_gateway_id) => {
    const payload = {
      action: 'trigger-accept-dx-gateway-association-proposals',
      detailed: {
        directConnectGatewayId: dx_gateway_id,
        proposalId: proposal_id,
        associatedGateway: {
          id: associated_gateway_id,
        },
      },
    };
    return await this.post('networkdb', payload, {}, {});
  };

  /**
   * @async
   * @name triggerAccountUpdate
   * @description
   * @param {string} account_id
   * @returns {Promise<object>} account details
   */
  triggerAccountUpdate = async account_id => {
    return await this.post('accounts/' + account_id + '/trigger-update');
  };

  /**
   * @async
   * @name triggerHostedZoneUpdate
   * @description
   * @param {string} fqdn
   * @returns {Promise<object>} account details
   */
  triggerHostedZoneUpdate = async fqdn => {
    const payload = {
      action: 'trigger-update-hostedzone',
      fqdn,
    };
    return await this.post('dns', payload);
  };

  /**
   * @async
   * @name triggerVPCUpdate
   * @description
   * @param {string} vpc_id
   * @returns {Promise<object>} The delete network item result from the backend
   */
  triggerVPCUpdate = async vpc_id => {
    const payload = {
      action: 'trigger-update-vpc',
      vpc_id,
    };
    return await this.post('networkdb', payload, {}, {});
  };

  /**
   * @async
   * @name updateOrder
   * @description
   * @param {string} order_id
   * @param {object} payload
   * @returns {Promise<object>} update-order response
   */
  updateOrder = async (order_id, payload) => {
    const combined_payload = Object.assign(
      {
        action: 'update-order',
        order_id,
      },
      payload,
    );
    return await this.post('orders', combined_payload);
  };

  /**
   * @async
   * @name verifySIEMKey
   * @description
   * @param {string} siem_key
   * @param {string} account_id
   * @returns {Promise<object>} key verification response
   */
  verifySIEMKey = async (siem_key, account_id) => {
    const payload = {
      siem_key: siem_key,
      account_id: account_id,
    };
    return await this.post('siem/verify', payload);
  };

  /**
   * @async
   * @name createSIEMKey
   * @description
   * @param {string} ola_handle
   * @returns {Promise<object>} create key response
   */
  createSIEMKey = async ola_handle => {
    const payload = {
      ola_handle: ola_handle,
    };
    return await this.post('siem/create', payload);
  };

  /**
   * @async
   * @name displayResponseAsAlert
   * @description
   * @param {Promise} requestPromise
   * @param {function} optionalCallback
   */
  displayResponseAsAlert = async (requestPromise, optionalCallback) => {
    requestPromise
      .then(result => {
        displaySuccessPopup(result.message);
      })
      .catch(displayErrorPopup)
      .finally(() => {
        removeSpinners();
        if (typeof optionalCallback === 'function') optionalCallback();
      });
  };

  /**
   * @async
   * @name displayResponseAsToast
   * @description
   * @param {Promise} requestPromise
   * @param {string} toastTitle
   * @param {function} optionalCallback
   */
  displayResponseAsToast = async (requestPromise, toastTitle, optionalCallback) => {
    requestPromise
      .then(result => {
        addToast(toastTitle, result.message, 5000);
      })
      .catch(displayErrorPopup)
      .finally(() => {
        removeSpinners();
        if (typeof optionalCallback === 'function') optionalCallback();
      });
  };

  /**
   * @async
   * @name delete
   * @description
   * @param {string} endpoint
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  delete = async (endpoint, payload, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.delete(endpoint, payload, config);
    } else response = await this.axiosInstance.delete(endpoint, payload);
    return response?.data;
  };

  /**
   * @async
   * @name get
   * @description
   * @param {string} endpoint
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  get = async (endpoint, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.get(endpoint, config);
    } else {
      response = await this.axiosInstance.get(endpoint);
    }
    return response?.data;
  };

  /**
   * @async
   * @name post
   * @description
   * @param {string} endpoint
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  post = async (endpoint, payload, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.post(endpoint, payload, config);
    } else response = await this.axiosInstance.post(endpoint, payload);
    return response?.data;
  };

  /**
   * @async
   * @name patch
   * @description
   * @param {string} endpoint
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  patch = async (endpoint, payload, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.patch(endpoint, payload, config);
    } else response = await this.axiosInstance.patch(endpoint, payload);
    return response?.data;
  };

  /**
   * @async
   * @name put
   * @description
   * @param {string} endpoint
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  put = async (endpoint, payload, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.put(endpoint, payload, config);
    } else response = await this.axiosInstance.put(endpoint, payload);
    return response?.data;
  };
}

export const baseApiAxios = new BaseApiAxios(apiAxios);
export /**
 * Axios request interceptor that adds a Bearer token to the Authorization header.
 *
 * This interceptor retrieves the user token from sessionStorage and adds it to the
 * request headers. If the user token is not set, the createAuthRefreshInterceptor
 * will handle the resulting 401 error.
 *
 * @param {Object} config - The axios request configuration object
 * @returns {Object} The modified axios request configuration with Authorization header
 */
const authBearerInterceptor = config => {
  // If the user token is not set,
  // then the createAuthRefreshInterceptor will handle the 401 error
  config.headers.Authorization = `Bearer ${sessionStorage.user_token}`;
  return config;
};

// Margin (seconds) before the expiry of the access token from which a 403 is no longer trusted to be a permission error
const EXPIRY_GRACE_SECONDS = 30;

// The access token a request went out with. A slow 401/403 must be judged by it, not by the token that
// sessionStorage holds by the time the response arrives.
function tokenSentWith(request) {
  const header = request?.headers?.Authorization;
  return typeof header === 'string' && header ? header.replace(/^Bearer /, '') : undefined;
}

// Shared refresh state — ensures only one refresh attempt runs at a time
// across all axios instances. Concurrent 401/403 errors will share the
// same refresh promise instead of firing multiple token refresh requests.
let _refreshPromise = null;

// Flag indicating a logout redirect is in progress. Once set, all subsequent
// errors are silently swallowed so no swal/modal popups appear while the
// page is navigating away.
let _logoutInProgress = false;

function logoutAndSwallow() {
  if (_logoutInProgress) return;
  _logoutInProgress = true;
  logoutFromIdp();
}

/**
 * Creates a response interceptor that handles 401/403 errors by:
 * 1. Attempting to refresh the access token using the refresh token
 * 2. Retrying the original request with the new token
 * 3. Logging out only if the refresh token is also expired/invalid
 *
 * @param {object} axiosInstance - The axios instance to use for retrying requests.
 *        This ensures retries go through the same interceptors as the original request.
 * @returns {function[]} - Success and error handlers for response interceptors
 */
export function createAuthRefreshInterceptor(axiosInstance) {
  // Success handler - just return the response
  const successHandler = response => response;

  // Error handler - attempt refresh, then retry or logout
  const errorHandler = async error => {
    // If a logout is already in progress, swallow ALL errors silently.
    // This prevents swal popups from concurrent in-flight requests that fail
    // after clearStorage() has wiped the tokens.
    if (_logoutInProgress) {
      return new Promise(() => {});
    }

    // Only handle axios errors with proper configuration
    if (!error.config) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const status = error.response?.status; // undefined for network errors

    // Handle request-level errors (e.g. token missing from sessionStorage after
    // clearStorage, or browser aborting in-flight requests during logout redirect).
    // These have no response object.
    if (!error.response) {
      // If the browser is online, this is likely a CORS-blocked 403 from an expired
      // token (backend returns 403 without CORS headers → browser blocks the response
      // → axios gets a NetworkError with no response object). Treat it like a 401/403
      // and attempt a token refresh + retry instead of letting it propagate to callers
      // (which would incorrectly trigger OpenSearch → DynamoDB fallback).
      if (navigator.onLine && error.isAxiosError && !originalRequest._retryAfterRefresh) {
        console.warn(`NetworkError (likely CORS-blocked 403) for ${originalRequest.url}. Attempting token refresh...`);
        // Fall through to the refresh logic below
      } else {
        return Promise.reject(error);
      }
    }

    // Only handle 401/403 (auth errors) or NetworkErrors that fell through
    // (CORS-blocked 403 — status is undefined because there's no response)
    if (status !== 401 && status !== 403 && error.response) {
      return Promise.reject(error);
    }

    // A 403 that comes with a readable response for a request whose access token was still valid cannot be an
    // expired session: the user simply isn't allowed to do this. Hand it to the caller instead of refreshing the
    // token and logging the user out. This also covers the retry with a freshly refreshed token.
    const sentToken = tokenSentWith(originalRequest);
    if (
      status === 403 &&
      !(sentToken ? isTokenExpiringSoon(EXPIRY_GRACE_SECONDS, sentToken) : isTokenExpiringSoon(EXPIRY_GRACE_SECONDS))
    ) {
      return Promise.reject(error);
    }

    // Don't retry if we already retried this request (prevents infinite loops)
    if (originalRequest._retryAfterRefresh) {
      console.warn(`Received ${status || 'NetworkError'} for ${originalRequest.url} after token refresh. Logging out.`);
      logoutAndSwallow();
      return new Promise(() => {});
    }

    // The token was already renewed after this request went out (e.g. a slow response after a cold start), so the
    // stored token is newer than the one that was rejected. Retry once with it instead of refreshing a second time.
    const currentToken = sessionStorage.getItem('user_token');
    const renewedSinceSent =
      !!sentToken && !!currentToken && currentToken !== sentToken && !isTokenExpiringSoon(EXPIRY_GRACE_SECONDS);

    if (renewedSinceSent) {
      console.warn(
        `Received ${status || 'NetworkError'} for ${originalRequest.url} after the token was renewed. Retrying...`,
      );
    } else {
      console.warn(`Received ${status || 'NetworkError'} for ${originalRequest.url}. Attempting token refresh...`);

      // Deduplicate concurrent refresh attempts — all 401/403 errors
      // that arrive while a refresh is in-flight will share this promise.
      // Track whether this request initiated the refresh (for toast dedup).
      let initiatedRefresh = false;
      if (!_refreshPromise) {
        initiatedRefresh = true;
        _refreshPromise = refreshAccessToken().finally(() => {
          _refreshPromise = null;
        });
      }

      const refreshed = await _refreshPromise;

      if (!refreshed) {
        // Refresh token is also expired/invalid — full logout
        console.warn('Token refresh failed. Both tokens expired, logging out.');
        logoutAndSwallow();
        return new Promise(() => {});
      }

      // Refresh succeeded — show toast only once (from the request that initiated the refresh)
      if (initiatedRefresh) {
        addToast(
          'Session Renewed',
          'Welcome Back! Your auth session has been refreshed automatically.',
          6000,
          'toast-success',
        );
      }
    }
    originalRequest._retryAfterRefresh = true;
    const newToken = sessionStorage.getItem('user_token');
    if (newToken) {
      // Update the Authorization header for the retry
      originalRequest.headers.Authorization = originalRequest.headers.Authorization?.startsWith('Bearer ')
        ? `Bearer ${newToken}`
        : newToken;
    }

    // Retry using the SAME axios instance so its interceptors still apply.
    // Wrap in try/catch so any error during the retry (e.g. request interceptor
    // rejection, network error) is caught and doesn't leak a swal popup to the
    // caller. Auth errors on the retry are already handled by _retryAfterRefresh
    // guard above (on the next interceptor pass). Non-auth errors are re-thrown
    // so callers can still handle genuine API failures.
    try {
      return await axiosInstance(originalRequest);
    } catch (retryError) {
      const retryStatus = retryError?.response?.status;
      // Still denied with a fresh token: a permission problem, not an expired session.
      if (retryStatus === 403) throw retryError;
      if (retryStatus === 401) {
        // Auth error on retry — shouldn't normally happen (interceptor handles
        // it via _retryAfterRefresh), but guard against edge cases.
        console.warn(`Retry also received ${retryStatus} for ${originalRequest.url}. Logging out.`);
        logoutAndSwallow();
        return new Promise(() => {});
      }
      // NetworkError on retry (e.g. CORS-blocked 403 again) — token refresh
      // didn't help, so logout rather than letting it propagate to callers
      // (which would incorrectly trigger OpenSearch → DynamoDB fallback).
      if (!retryError.response && retryError.isAxiosError && navigator.onLine) {
        console.warn(`Retry NetworkError for ${originalRequest.url}. Token refresh did not resolve. Logging out.`);
        logoutAndSwallow();
        return new Promise(() => {});
      }
      // Non-auth error — let it propagate so callers can display appropriate messages.
      throw retryError;
    }
  };

  return [successHandler, errorHandler];
}

/**
 * @class MicroservicesApiAxios
 * @description A class that provides an API client for interacting with microservices using Axios.
 * It handles authentication, request/response interceptors, and provides methods for common HTTP operations.
 *
 * @property {object} axiosInstance - The configured Axios instance used for making HTTP requests
 *
 * @method get - Performs a GET request to the specified endpoint
 * @method post - Performs a POST request to the specified endpoint with optional payload
 * @method delete - Performs a DELETE request to the specified endpoint with optional payload
 * @method patch - Performs a PATCH request to the specified endpoint with optional payload
 *
 * @method getSearchOrders - Retrieves order search results
 * @method getSearchOrdersTypeahead - Retrieves typeahead suggestions for order search
 * @method getSearchOrdersNumbersLastWeek - Retrieves order statistics for the past week
 * @method getSearchVPCs - Retrieves VPC search results
 * @method getSearchVPCsTypeahead - Retrieves typeahead suggestions for VPC search
 * @method getVPCsNumbers - Retrieves VPC statistics and counts
 * @method getSearchHostedZones - Retrieves hosted zone search results
 * @method getSearchFourWheels - Retrieves FourWheels cluster search results
 */
class MicroservicesApiAxios {
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: CONF.apiEndpoint.replace('/v1', ''),
    });
    this.axiosInstance.interceptors.request.use(authBearerInterceptor);
    this.axiosInstance.interceptors.response.use(...createAuthRefreshInterceptor(this.axiosInstance));
  }

  /**
   * @async
   * @name get
   * @description
   * @param {string} endpoint
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  get = async (endpoint, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.get(endpoint, config);
    } else {
      response = await this.axiosInstance.get(endpoint);
    }
    return response?.data;
  };

  /**
   * @async
   * @name post
   * @description
   * @param {string} endpoint
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  post = async (endpoint, payload, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.post(endpoint, payload, config);
    } else response = await this.axiosInstance.post(endpoint, payload);
    return response?.data;
  };

  /**
   * @async
   * @name delete
   * @description
   * @param {string} endpoint
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  delete = async (endpoint, payload, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.delete(endpoint, payload, config);
    } else response = await this.axiosInstance.delete(endpoint, payload);
    return response?.data;
  };

  /**
   * @async
   * @name patch
   * @description
   * @param {string} endpoint
   * @param {object} payload
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} The response from the backend
   */
  patch = async (endpoint, payload, headers, params) => {
    let response;
    if (headers || params) {
      const config = Object.assign({}, headers && { headers }, params && { params });
      response = await this.axiosInstance.patch(endpoint, payload, config);
    } else response = await this.axiosInstance.patch(endpoint, payload);
    return response?.data;
  };

  /**
   * @async
   * @name getSearchOrders
   * @description Retrieves order search results from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} Order search results data
   */
  getSearchOrders = async (headers, params) => {
    return await this.get('api/search/orders', headers, params);
  };

  /**
   * @async
   * @name getSearchOrdersTypeahead
   * @description Retrieves typeahead suggestions for order search from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} Order search typeahead suggestions
   */
  getSearchOrdersTypeahead = async (headers, params) => {
    return await this.get('api/search/orders/typeahead', headers, params);
  };

  /**
   * @async
   * @name getSearchOrdersNumbersLastWeek
   * @description Retrieves order statistics for the past week from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} Order numbers statistics for the last week
   */
  getSearchOrdersNumbersLastWeek = async (headers, params) => {
    return await this.get('api/search/orders/numbers-last-week', headers, params);
  };

  /**
   * @async
   * @name getSearchVPCs
   * @description Retrieves VPC search results from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} VPC search results data
   */
  getSearchVPCs = async (headers, params) => {
    return await this.get('api/search/vpcs', headers, params);
  };

  /**
   * @async
   * @name getSearchHostedZones
   * @description
   * @param {object} headers
   * @param {object} params
   * @returns {Promise<object>} hosted zone datasets
   */
  getSearchHostedZones = async (headers, params) => {
    return await this.get(`api/search/dns`, headers, params);
  };

  /**
   * @async
   * @name getSearchHostedZonesTypeahead
   * @description Retrieves typeahead suggestions for DNS search from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} DNS search typeahead suggestions
   */
  getSearchHostedZonesTypeahead = async (headers, params) => {
    return await this.get('api/search/dns/typeahead', headers, params);
  };

  /**
   * @async
   * @name getSearchVPCsTypeahead
   * @description Retrieves typeahead suggestions for VPC search from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} VPC search typeahead suggestions
   */
  getSearchVPCsTypeahead = async (headers, params) => {
    return await this.get('api/search/vpcs/typeahead', headers, params);
  };

  /**
   * @async
   * @name getVPCsNumbers
   * @description Retrieves VPC statistics and counts from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} VPC counts and statistics data
   */
  getVPCsNumbers = async (headers, params) => {
    return await this.get('api/search/vpcs/numbers', headers, params);
  };

  /**
   * @async
   * @name getSearchAccounts
   * @description Retrieves Accounts search results from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} Accounts search results data
   */
  getSearchAccounts = async (headers, params) => {
    return await this.get('api/search/accounts', headers, params);
  };

  /**
   * @async
   * @name getSearchAccountsTypeahead
   * @description Retrieves Accounts search results from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} Accounts search results data
   */
  getSearchAccountsTypeahead = async (headers, params) => {
    return await this.get('api/search/accounts/typeahead', headers, params);
  };

  /**
   * @async
   * @name getSearchFourWheels
   * @description Retrieves FourWheels search results from the search microservice
   * @param {object} headers - Request headers to be included in the API call
   * @param {object} params - Request parameters to be included in the API call
   * @returns {Promise<object>} Fourwheels search results data
   */
  getSearchFourWheels = async (headers, params) => {
    return await this.get('api/search/fourwheels', headers, params);
  };

  getIDCSSOSignInRoleName = async (accountId, roleName) => {
    return await this.get(`api/idc/accounts/${accountId}/role_name_signin_url/${roleName}`, null, null);
  };

  getIDCAccountAccessLoginDetails = async accountId => {
    return await this.get(`api/idc/accounts/${accountId}/account_access_login_details`, null, null);
  };

  getIDCRightNowPortalQueueItems = async () => {
    return await this.get(`api/idc/right_now_portal_queue`, null, null);
  };
}

export const microservicesApiAxios = new MicroservicesApiAxios();
